import { Medal, Trophy, Flag, Users, Timer, Waves, Dumbbell, Target, Award, Star } from "lucide-react";
import { cn } from "@/lib/utils";

const KIND = {
  gold:   { color: "#D6A73A", label: "Or",     icon: Medal },
  silver: { color: "#B9C2CC", label: "Argent", icon: Medal },
  bronze: { color: "#B07A4A", label: "Bronze", icon: Medal },
  trophy: { color: "#0E7490", label: "Résultat", icon: Trophy },
  major:  { color: "#0E7490", label: "Titre majeur", icon: Award },
};

// Icônes par famille — pour donner un signal visuel discipline-aware
const FAMILY_ICON = {
  combat: Trophy,
  team: Users,
  timed: Timer,
  vaa: Waves,
  strength: Dumbbell,
  score_judge: Star,
  racket: Target,
  shooting: Target,
  generic: Trophy,
};

function inferKind(medal, title = "", level = "", rank = null) {
  // Priorité au champ medal explicite
  const m = (medal || "").toLowerCase();
  if (m === "or" || m === "gold") return "gold";
  if (m === "argent" || m === "silver") return "silver";
  if (m === "bronze") return "bronze";
  // Puis au rank
  if (rank === 1) return "gold";
  if (rank === 2) return "silver";
  if (rank === 3) return "bronze";
  // Fallback texte
  const t = `${title} ${level}`.toLowerCase();
  if (/champion|médaille d['’]or|olympi/.test(t)) return "gold";
  if (/vice-champion|2e |second/.test(t)) return "silver";
  if (/3e |troisième/.test(t)) return "bronze";
  return "trophy";
}

/**
 * V11.6 — Composant discipline-aware.
 * Affiche les métadonnées les plus utiles selon `sport_family`.
 * Rétro-compatible : accepte toutes les anciennes props.
 */
export function MedalCard({
  title, year, month, level, place, source_url, className,
  // V11.6 fields
  medal, rank, sport_family, discipline, is_major_title, is_collective,
  team_name, opponent, match_outcome,
  time_performance, distance, crew_format,
  weight, belt, age_class, category, competition,
}) {
  const kind = inferKind(medal, title, level, rank);
  const data = KIND[kind] || KIND.trophy;
  const Icon = is_major_title ? Award : data.icon;
  const FamilyIcon = FAMILY_ICON[sport_family] || FAMILY_ICON.generic;

  // Metadata lignes par famille
  const metaLines = [];
  if (sport_family === "combat") {
    if (weight) metaLines.push(<span key="w"><strong>{weight}</strong></span>);
    if (age_class) metaLines.push(<span key="a">{age_class}</span>);
    if (belt) metaLines.push(<span key="b">🥋 {belt}</span>);
    if (match_outcome) metaLines.push(<span key="mo" className={match_outcome === "victoire" ? "text-emerald-600" : "text-muted-foreground"}>{match_outcome}</span>);
    if (opponent) metaLines.push(<span key="op" className="italic">vs {opponent}</span>);
  } else if (sport_family === "team") {
    if (team_name) metaLines.push(<span key="tn"><Users className="inline h-3 w-3 mr-0.5" /><strong>{team_name}</strong></span>);
    if (category) metaLines.push(<span key="c">{category}</span>);
  } else if (sport_family === "timed") {
    if (distance) metaLines.push(<span key="d"><strong>{distance}</strong></span>);
    if (time_performance) metaLines.push(<span key="t" className="font-mono text-[hsl(var(--aito-lagoon))]">{time_performance}</span>);
    if (category) metaLines.push(<span key="c">{category}</span>);
  } else if (sport_family === "vaa") {
    if (crew_format) metaLines.push(<span key="cf"><strong>{crew_format}</strong></span>);
    if (distance) metaLines.push(<span key="d">{distance}</span>);
    if (team_name) metaLines.push(<span key="tn"><Waves className="inline h-3 w-3 mr-0.5" />{team_name}</span>);
    if (time_performance) metaLines.push(<span key="t" className="font-mono text-[hsl(var(--aito-lagoon))]">{time_performance}</span>);
  } else if (sport_family === "strength") {
    if (weight) metaLines.push(<span key="w"><strong>{weight}</strong></span>);
    if (category) metaLines.push(<span key="c">{category}</span>);
  } else if (sport_family === "racket") {
    if (category) metaLines.push(<span key="c">{category}</span>);
    if (team_name) metaLines.push(<span key="p">👥 {team_name}</span>);
  } else {
    if (category) metaLines.push(<span key="c">{category}</span>);
  }

  return (
    <div data-testid="medal-card" className={cn("relative overflow-hidden rounded-xl border bg-card p-4 aito-card-hover", className)}>
      <span className="absolute left-0 top-0 h-full w-1" style={{ backgroundColor: data.color }} />
      {is_major_title && (
        <span
          className="absolute top-2 right-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
          style={{ backgroundColor: "#D6A73A", color: "#000" }}
          data-testid="medal-major-badge"
          title="Titre majeur"
        >
          ⭐ Titre majeur
        </span>
      )}
      <div className="flex items-start gap-3">
        <div className="flex flex-col items-center gap-1">
          <Icon className="h-5 w-5" style={{ color: data.color }} />
          {sport_family && sport_family !== "generic" && (
            <FamilyIcon className="h-3.5 w-3.5 text-muted-foreground" title={sport_family} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-sm font-semibold leading-tight" data-testid="medal-type">
            {title}
            {rank && !medal && <span className="ml-1.5 text-xs font-mono text-muted-foreground">· {rank}<sup>e</sup></span>}
            {is_collective && (
              <span className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] uppercase font-normal tracking-wider text-muted-foreground border rounded px-1 py-0.5">
                <Users className="h-2.5 w-2.5" /> collectif
              </span>
            )}
          </div>
          {competition && (
            <div className="text-[11px] text-muted-foreground mt-0.5 truncate italic">{competition}</div>
          )}
          <div className="text-xs text-muted-foreground mt-1 font-mono flex items-center gap-2 flex-wrap">
            {year && (
              <span>
                {year}{month ? `/${String(month).padStart(2, "0")}` : ""}
              </span>
            )}
            {level && <span className="capitalize">{level.replace(/-/g, " ")}</span>}
            {place && <span>📍 {place}</span>}
          </div>
          {metaLines.length > 0 && (
            <div className="mt-1.5 text-xs flex items-center gap-2 flex-wrap" data-testid="medal-family-meta">
              {metaLines.map((el, i) => (
                <span key={i} className="inline-flex items-center">{el}</span>
              ))}
            </div>
          )}
          {source_url && (
            <a
              href={source_url}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-block text-xs text-[hsl(var(--aito-lagoon))] hover:underline"
              data-testid="medal-source-link"
            >
              Voir la source
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
